export const productInCategory = (product, categoryId) => {
  if (!product || !categoryId) {
    return false;
  }
  const targetId = categoryId.toString().trim().toLowerCase();

  // 1. parentCategory
  const parentCatId = product.parentCategory?.id?.toString().trim().toLowerCase();
  const parentCatName = product.parentCategory?.name?.toString().trim().toLowerCase();
  if (parentCatId === targetId || parentCatName === targetId) {
    return true;
  }

  // 2. categories array
  if (Array.isArray(product.categories)) {
    const found = product.categories.some((item) => {
      if (typeof item === "string") {
        return item.trim().toLowerCase() === targetId;
      }
      if (item && typeof item === "object") {
        const itemId = item.id?.toString().trim().toLowerCase();
        const itemName = item.name?.toString().trim().toLowerCase();
        return itemId === targetId || itemName === targetId;
      }
      return false;
    });
    if (found) return true;
  }

  // 3. legacy product.category
  if (product.category) {
    if (typeof product.category === "string") {
      if (product.category.trim().toLowerCase() === targetId) return true;
    } else if (typeof product.category === "object") {
      const cId = product.category.id?.toString().trim().toLowerCase();
      const cName = product.category.name?.toString().trim().toLowerCase();
      if (cId === targetId || cName === targetId) return true;
    }
  }

  return false;
};

export const categoryHasDirectProducts = (categoryId, products = []) => {
  if (!categoryId) {
    return false;
  }
  return products.some((product) => productInCategory(product, categoryId));
};

export const categoryHasProducts = (
  categoryId,
  products = [],
  categories = []
) => {
  if (!categoryId) {
    return false;
  }
  if (categoryHasDirectProducts(categoryId, products)) {
    return true;
  }
  const targetId = categoryId.toString().trim().toLowerCase();
  const cats = Array.isArray(categories) ? categories : [];
  return cats.some((category) => {
    if (!category || !category.parentCategory) return false;
    const parentId = category.parentCategory.id?.toString().trim().toLowerCase();
    const parentName = category.parentCategory.name?.toString().trim().toLowerCase();
    const matchesParent =
      (parentId && parentId === targetId) ||
      (parentName && parentName === targetId);
    return (
      matchesParent && categoryHasProducts(category.id, products, categories)
    );
  });
};

export const getVisibleSubcategories = (
  categoryId,
  categories = [],
  products = [],
  includeHidden = true,
  requireProducts = true
) => {
  if (!categoryId || !Array.isArray(categories)) {
    return [];
  }
  const targetId = categoryId.toString().trim().toLowerCase();
  const prods = Array.isArray(products) ? products : [];

  return categories.filter((item) => {
    if (!item || !item.parentCategory) return false;
    const parentCat = item.parentCategory;
    const parentId = parentCat.id?.toString().trim().toLowerCase();
    const parentName = parentCat.name?.toString().trim().toLowerCase();
    const matchesParent =
      (parentId && parentId === targetId) ||
      (parentName && parentName === targetId);

    if (!matchesParent) return false;

    if (!includeHidden && item.showInNavbar !== true) {
      return false;
    }

    if (requireProducts && !categoryHasDirectProducts(item.id, prods)) {
      return false;
    }

    return true;
  });
};

export const getProductListCategoryIds = (
  categoryId,
  categories = [],
  products = [],
  includeHidden = true,
  requireProducts = true
) => {
  const subCategories = getVisibleSubcategories(
    categoryId,
    categories,
    products,
    includeHidden,
    requireProducts
  );
  return [categoryId, ...subCategories.map((item) => item.id)];
};

export const filterProductsForCategory = (
  products = [],
  categoryId,
  categories = [],
  includeHidden = true,
  requireProducts = true
) => {
  const categoryIds = getProductListCategoryIds(
    categoryId,
    categories,
    products,
    includeHidden,
    requireProducts
  );
  return products.filter((product) =>
    categoryIds.some((id) => productInCategory(product, id))
  );
};

export const groupProductsBySubcategories = (
  products = [],
  subCategories = []
) => {
  const assigned = new Set();
  const groups = [];

  subCategories.forEach((subCategory) => {
    const items = products.filter((product) =>
      productInCategory(product, subCategory.id)
    );
    if (!items.length) {
      return;
    }
    items.forEach((product) => assigned.add(product.id));
    groups.push({
      id: subCategory.id,
      name: subCategory.name,
      products: items,
    });
  });

  const ungrouped = products.filter((product) => !assigned.has(product.id));
  return { groups, ungrouped };
};

export const isProductUsingCategory = (product, category) => {
  if (!product || !category) return false;
  const catId = category.id?.toString().trim();
  const catName = category.name?.toString().trim();

  // 1. Check parentCategory by ID or Name
  if (
    catId &&
    product.parentCategory?.id &&
    product.parentCategory.id.toString().trim().toLowerCase() === catId.toLowerCase()
  ) {
    return true;
  }
  if (
    catName &&
    product.parentCategory?.name &&
    product.parentCategory.name.toString().trim().toLowerCase() === catName.toLowerCase()
  ) {
    return true;
  }

  // 2. Check categories array
  if (Array.isArray(product.categories)) {
    const found = product.categories.some((item) => {
      if (typeof item === "string") {
        const itemLower = item.trim().toLowerCase();
        return (
          (catId && itemLower === catId.toLowerCase()) ||
          (catName && itemLower === catName.toLowerCase())
        );
      } else if (item && typeof item === "object") {
        const itemId = item.id?.toString().trim().toLowerCase();
        const itemName = item.name?.toString().trim().toLowerCase();
        return (
          (catId && itemId === catId.toLowerCase()) ||
          (catName && itemName === catName.toLowerCase())
        );
      }
      return false;
    });
    if (found) return true;
  }

  // 3. Check legacy category field
  if (product.category) {
    if (typeof product.category === "string") {
      const trimmed = product.category.trim().toLowerCase();
      if (
        (catId && trimmed === catId.toLowerCase()) ||
        (catName && trimmed === catName.toLowerCase())
      ) {
        return true;
      }
    } else if (typeof product.category === "object") {
      const cId = product.category.id?.toString().trim().toLowerCase();
      const cName = product.category.name?.toString().trim().toLowerCase();
      if (
        (catId && cId === catId.toLowerCase()) ||
        (catName && cName === catName.toLowerCase())
      ) {
        return true;
      }
    }
  }

  return false;
};

export const getProductsUsingCategory = (products = [], category) => {
  if (!category || !Array.isArray(products)) return [];
  return products.filter((p) => isProductUsingCategory(p, category));
};

export const getCategoryImageUrl = (category) => {
  if (!category) return null;

  // Direct string properties
  if (typeof category.imageUrl === "string" && category.imageUrl.trim()) {
    return category.imageUrl.trim();
  }
  if (typeof category.image === "string" && category.image.trim()) {
    return category.image.trim();
  }
  if (
    category.image?.url &&
    typeof category.image.url === "string" &&
    category.image.url.trim()
  ) {
    return category.image.url.trim();
  }
  if (typeof category.thumbnail === "string" && category.thumbnail.trim()) {
    return category.thumbnail.trim();
  }
  if (
    category.thumbnail?.url &&
    typeof category.thumbnail.url === "string" &&
    category.thumbnail.url.trim()
  ) {
    return category.thumbnail.url.trim();
  }

  // Array of images
  if (Array.isArray(category.images) && category.images.length > 0) {
    for (const img of category.images) {
      if (typeof img === "string" && img.trim()) {
        return img.trim();
      }
      if (img && typeof img === "object") {
        if (typeof img.url === "string" && img.url.trim()) {
          return img.url.trim();
        }
        if (typeof img.imageUrl === "string" && img.imageUrl.trim()) {
          return img.imageUrl.trim();
        }
        if (typeof img.src === "string" && img.src.trim()) {
          return img.src.trim();
        }
      }
    }
  }

  return null;
};

